import React, { useState } from 'react';

import { Button, Flex, Modal, ScrollArea } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useQuery } from '@tanstack/react-query';
import { ExternalPhoneNumberTable } from 'components/SmartAssistantSections/TelephonySection/PurchasePhoneNumberModal/ExternalPhoneNumberTable';
import { InternationalPhoneNumberInfoAlert } from 'components/SmartAssistantSections/TelephonySection/PurchasePhoneNumberModal/InternationlPhoneNumbersAlert';
import { OutOfFreePhoneNumbersAlert } from 'components/SmartAssistantSections/TelephonySection/PurchasePhoneNumberModal/OutOfFreePhoneNumbersAlert';
import {
  PhoneNumberSearchBar,
  PhoneNumberSearchBarFormProps
} from 'components/SmartAssistantSections/TelephonySection/PurchasePhoneNumberModal/PhoneNumberSearchBar';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import { useTranslation } from 'react-i18next';
import { TelephonyService } from 'services/telephony-service';
import { UsageService } from 'services/usage-service';
import { EXTERNAL_PHONE_NUMBERS_QUERY_KEY, USER_USAGE_QUERY_KEY } from 'shared/constants/query-keys';
import { PhoneNumberProvider, PurchasePhoneNumberDto } from 'shared/interfaces/Telephony';

interface PurchasePhoneNumberModalProps {
  opened: boolean;
  onClose: () => void;
  onPurchaseConfirm: (purchasePhoneNumberDto: PurchasePhoneNumberDto) => void;
}

export function PurchasePhoneNumberModal(purchasePhoneNumberModalProps: PurchasePhoneNumberModalProps) {
  const { opened, onClose, onPurchaseConfirm } = purchasePhoneNumberModalProps;
  const { t } = useTranslation();
  const [selectedNumber, setSelectedNumber] = useState('');
  const [subscription] = useSubscriptionContext();

  const searchForm = useForm<PhoneNumberSearchBarFormProps>({
    initialValues: {
      countryCode: 'US',
      searchQuery: '',
      provider: PhoneNumberProvider.TWILIO
    }
  });

  const usageQuery = useQuery({
    queryKey: [USER_USAGE_QUERY_KEY],
    queryFn: () => UsageService.getUsage()
  });

  const externalPhoneNumbersQuery = useQuery({
    queryKey: [EXTERNAL_PHONE_NUMBERS_QUERY_KEY, searchForm.values],
    queryFn: () => TelephonyService.getExternalPhoneNumbers(searchForm.values)
  });

  const handleContinue = () => {
    if (selectedNumber) {
      onPurchaseConfirm({ phoneNumber: selectedNumber, provider: searchForm.values.provider });
    }
  };

  const externalPhoneNumbers = externalPhoneNumbersQuery.data || [];
  const showOutOfFreePhoneNumbersAlert =
    usageQuery.data?.totalProvidedPhoneNumbersCount !== undefined &&
    usageQuery.data.totalProvidedPhoneNumbersCount >= subscription.product.totalProvidedPhoneNumbersLimit;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('SMART_ASSISTANT.TELEPHONY_SECTION.PURCHASE_MODAL.TITLE')}
      size="lg">
      <Flex direction="column" gap="md">
        <InternationalPhoneNumberInfoAlert />
        {showOutOfFreePhoneNumbersAlert && <OutOfFreePhoneNumbersAlert />}
        <PhoneNumberSearchBar searchForm={searchForm} />
        <ScrollArea style={{ height: 300 }} scrollbarSize={0}>
          <ExternalPhoneNumberTable
            externalPhoneNumbers={externalPhoneNumbers}
            selectedNumber={selectedNumber}
            onSelectNumber={setSelectedNumber}
            isLoading={externalPhoneNumbersQuery.isLoading}
          />
        </ScrollArea>
        <Flex justify="space-between" align="center" mt="md">
          <Button variant="outline" onClick={onClose}>
            {t('SMART_ASSISTANT.TELEPHONY_SECTION.PURCHASE_MODAL.BUTTONS.CANCEL')}
          </Button>
          <Button onClick={handleContinue} disabled={!selectedNumber}>
            {t('SMART_ASSISTANT.TELEPHONY_SECTION.PURCHASE_MODAL.BUTTONS.CONTINUE')}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
}
